import { logger } from "@/utils/logger";

export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

export const setCookie = (name: string, value: string, days: number = 365): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

export const checkOptanonConsent = (): boolean => {
  const optanonCookie = getCookie('OptanonConsent');
  if (!optanonCookie) return true; // Assume consent if OneTrust is not present
  
  // Check if C0002 (Performance Cookies) is set to 1
  const match = optanonCookie.match(/groups=([^&]*)/);
  if (!match) return false;
  
  const groups = decodeURIComponent(match[1]);
  return groups.includes('C0002:1');
};

export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
};

export const setD2CChatbotSessionCookie = (sessionId: string): void => {
  // Session continuity cookie written only AFTER the visitor's first interaction.
  // 90-day expiry. Also purges the deprecated 1-year `d2cchatbotid` cookie.
  setCookie('d2c_chatbot_session', sessionId, 90);
  deleteCookie('d2cchatbotid');
};

/**
 * @deprecated Use setD2CChatbotSessionCookie instead. Kept as a shim during refactor.
 */
export const setD2CChatbotId = (sessionId: string): void => {
  logger.warn('[cookieUtils] setD2CChatbotId is deprecated; use setD2CChatbotSessionCookie.');
  setD2CChatbotSessionCookie(sessionId);
};

export const getActiveVehicleID = (): string | null => {
  return getCookie('activevehicle');
};

export const getUTMSource = (): string => {
  return getCookie('utm_source') || 'Google';
};

export const getUTMCampaign = (): string => {
  return getCookie('utm_campaign') || 'Organic';
};

export const setChatbotTestMode = (): void => {
  setCookie('chatbottest', 'true', 365); // Set for 1 year
};

export const isChatbotTestMode = (): boolean => {
  return getCookie('chatbottest') === 'true';
};

export const getD2CChatbotCookie = (): any | null => {
  const cookieValue = getCookie('d2cchatbot');
  if (!cookieValue) return null;
  
  try {
    return JSON.parse(decodeURIComponent(cookieValue));
  } catch (error) {
    console.error('Failed to parse d2cchatbot cookie:', error);
    return null;
  }
};

const stripDomain = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  
  try {
    // Try parsing as full URL
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    // If it's not a valid URL, check if it starts with a domain pattern
    // Remove leading domain like "/www.domain.com/" or "/domain.com/"
    const domainPattern = /^\/?((?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,})/;
    const match = url.match(domainPattern);
    
    if (match) {
      // Remove the domain part and return just the path
      return url.replace(domainPattern, '');
    }
    
    // Otherwise return as-is
    return url;
  }
};

export const storeServiceFormUrls = (): void => {
  const d2cCookie = getD2CChatbotCookie();
  if (d2cCookie && (d2cCookie.ServiceFormFr || d2cCookie.ServiceFormEng)) {
    const serviceFormUrls = {
      fr: stripDomain(d2cCookie.ServiceFormFr),
      en: stripDomain(d2cCookie.ServiceFormEng)
    };
    localStorage.setItem('serviceFormUrls', JSON.stringify(serviceFormUrls));
  }
};

const readFromLocalStorage = (language: 'en' | 'fr'): string | null => {
  try {
    const stored = localStorage.getItem('serviceFormUrls');
    if (!stored) return null;
    const urls = JSON.parse(stored);
    return (language === 'fr' ? urls.fr : urls.en) || null;
  } catch {
    return null;
  }
};

export const getServiceFormUrl = (language: 'en' | 'fr'): string | null => {
  // 1. Try localStorage cache
  let url = readFromLocalStorage(language);
  if (url) return url;

  // 2. Warm the cache from the d2cchatbot cookie and retry
  try {
    storeServiceFormUrls();
    url = readFromLocalStorage(language);
    if (url) return url;
  } catch {
    // ignore and fall through
  }

  // 3. Last-resort: read the cookie directly
  try {
    const d2cCookie = getD2CChatbotCookie();
    if (d2cCookie) {
      const raw = language === 'fr' ? d2cCookie.ServiceFormFr : d2cCookie.ServiceFormEng;
      return stripDomain(raw) || null;
    }
  } catch (error) {
    console.error('Failed to read service form URL from cookie:', error);
  }

  return null;
};
